import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useMatch } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import {
  KeyRound,
  Library,
  LogOut,
  MoreHorizontal,
  PanelLeftClose,
  Search,
  Shield,
  SquarePen,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useConversations, useMeta } from '@/api/queries'
import { PasswordForm } from '@/auth/AuthScreens'
import { useAuth } from '@/auth/context'
import { cn } from '@/lib/cn'
import { groupConversations } from '@/lib/time'
import { useNewChat } from '@/hooks/useNewChat'
import { useChatStore } from '@/stores/chatStore'
import { useUiStore } from '@/stores/uiStore'
import { spring } from '@/lib/motion'
import { AiOrb } from '@/components/ui/AiOrb'
import { Modal } from '@/components/ui/Dialog'
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from '@/components/ui/Menu'
import { IconButton } from '@/components/ui/IconButton'
import { Kbd } from '@/components/ui/Kbd'
import { Skeleton } from '@/components/ui/Skeleton'
import { ActivePill } from './ActivePill'
import { ConversationItem } from './ConversationItem'
import { ThemeToggle } from './ThemeToggle'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
const MOD = isMac ? '⌘' : 'Ctrl'
/** Focus requests already handled, so remounting the drawer doesn't re-focus search. */
let handledSearchNonce = 0

export function Sidebar({ inDrawer = false }: { inDrawer?: boolean }) {
  const meta = useMeta()
  const newChat = useNewChat()
  const toggleSidebar = useUiStore((s) => s.toggleSidebar)
  const closeDrawer = useUiStore((s) => s.setMobileSidebarOpen)
  const searchNonce = useUiStore((s) => s.searchFocusNonce)
  const activeConversations = useChatStore((s) => s.activeConversations)
  const match = useMatch('/c/:id')
  const activeId = match?.params.id ?? null
  const { data, isPending, isError, refetch } = useConversations()
  const [filter, setFilter] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (searchNonce <= handledSearchNonce) return
    handledSearchNonce = searchNonce
    // Defer so a drawer's own autofocus doesn't win.
    const frame = requestAnimationFrame(() => searchRef.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [searchNonce])

  const groups = useMemo(() => {
    const query = filter.trim().toLowerCase()
    const list = data ?? []
    return groupConversations(query ? list.filter((c) => c.title.toLowerCase().includes(query)) : list)
  }, [data, filter])

  const onNavigate = inDrawer ? () => closeDrawer(false) : undefined

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex h-14 items-center justify-between gap-2 px-3">
        <NavLink
          to="/"
          onClick={(event) => {
            event.preventDefault()
            newChat()
          }}
          className="group/brand flex min-w-0 items-center gap-2.5 rounded-xl px-1 py-1"
        >
          <AiOrb className="size-7 transition-transform duration-500 ease-out group-hover/brand:scale-110" />
          <span className="min-w-0 leading-tight">
            <span className="block truncate text-[14px] font-semibold tracking-snug text-ink">
              {meta.assistant_name}
            </span>
            <span className="block truncate text-[11px] text-ink-faint">{meta.org_name}</span>
          </span>
        </NavLink>
        {inDrawer ? (
          <IconButton label="Close menu" tooltip={false} onClick={() => closeDrawer(false)}>
            <X />
          </IconButton>
        ) : (
          <IconButton label="Collapse sidebar" onClick={toggleSidebar}>
            <PanelLeftClose />
          </IconButton>
        )}
      </div>

      <div className="space-y-2 px-3 pb-2">
        <motion.button
          type="button"
          onClick={newChat}
          whileTap={{ scale: 0.97 }}
          transition={spring.snappy}
          className="flex h-9 w-full items-center gap-2 rounded-xl bg-surface px-3 text-[13px] font-medium text-ink shadow-card transition-shadow duration-300 hover:shadow-lift"
        >
          <SquarePen className="size-4 text-accent-text" aria-hidden />
          <span className="flex-1 text-left">New chat</span>
          <span className="hidden gap-0.5 md:inline-flex" aria-hidden>
            <Kbd>{MOD}</Kbd>
            <Kbd>⇧</Kbd>
            <Kbd>O</Kbd>
          </span>
        </motion.button>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-faint"
            aria-hidden
          />
          <input
            ref={searchRef}
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && filter) {
                e.stopPropagation()
                setFilter('')
              }
            }}
            placeholder="Search chats"
            aria-label="Search chats"
            className="h-9 w-full rounded-xl bg-ink/[0.05] pl-8 pr-12 text-[13px] text-ink transition-shadow duration-200 placeholder:text-ink-faint focus:bg-surface focus:shadow-[0_0_0_3px_rgb(var(--accent)/0.25)] focus:outline-none dark:bg-white/[0.06]"
          />
          <span
            className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 gap-0.5 ${filter ? 'hidden' : 'hidden md:inline-flex'}`}
            aria-hidden
          >
            <Kbd>{MOD}</Kbd>
            <Kbd>K</Kbd>
          </span>
        </div>
      </div>

      <nav aria-label="Conversations" className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {isPending && (
          <div className="space-y-2 px-1 pt-4" aria-label="Loading conversations">
            {[70, 85, 60, 75, 50].map((w, i) => (
              <Skeleton key={i} className="h-7" style={{ width: `${w}%` }} />
            ))}
          </div>
        )}
        {isError && (
          <div className="px-2 pt-4 text-sm text-ink-muted">
            Couldn&rsquo;t load chats.{' '}
            <button
              type="button"
              className="font-medium text-accent-text hover:underline"
              onClick={() => void refetch()}
            >
              Retry
            </button>
          </div>
        )}
        {!isPending && !isError && groups.length === 0 && (
          <p className="px-2 pt-4 text-sm text-ink-faint">{filter ? 'No chats match your search.' : 'No chats yet.'}</p>
        )}
        {groups.map((group) => (
          <section key={group.label} aria-label={group.label} className="pt-3">
            <h2 className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
              {group.label}
            </h2>
            <ul className="space-y-px">
              <AnimatePresence initial={false}>
                {group.items.map((conversation) => (
                  <ConversationItem
                    key={conversation.id}
                    conversation={conversation}
                    active={conversation.id === activeId}
                    streaming={Boolean(activeConversations[conversation.id])}
                    onNavigate={onNavigate}
                  />
                ))}
              </AnimatePresence>
            </ul>
          </section>
        ))}
      </nav>

      <UserFooter onNavigate={onNavigate} />
    </div>
  )
}

function NavItem({
  to,
  icon: Icon,
  label,
  onNavigate,
}: {
  to: string
  icon: LucideIcon
  label: string
  onNavigate?: () => void
}) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'relative mb-1 flex h-9 items-center gap-2 rounded-[10px] px-2.5 text-[13.5px] transition-colors',
          isActive
            ? 'font-medium text-ink'
            : 'text-ink-muted hover:bg-ink/[0.04] hover:text-ink dark:hover:bg-white/[0.05]',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <ActivePill />}
          <Icon className="relative size-4" aria-hidden />
          <span className="relative">{label}</span>
        </>
      )}
    </NavLink>
  )
}

const ROLE_LABEL = { admin: 'Admin', contributor: 'Contributor', member: 'Member' } as const

function UserFooter({ onNavigate }: { onNavigate?: () => void }) {
  const meta = useMeta()
  const { signOut, accountName, mode } = useAuth()
  const [changingPassword, setChangingPassword] = useState(false)
  const name = meta.user?.name ?? accountName ?? 'Signed in'
  const initials = name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div className="p-2 shadow-[inset_0_0.5px_0_rgb(var(--ink)/0.08)]">
      <NavItem to="/documents" icon={Library} label="Documents" onNavigate={onNavigate} />
      {meta.user?.is_admin && <NavItem to="/admin" icon={Shield} label="Admin" onNavigate={onNavigate} />}
      <div className="flex items-center gap-2 px-1 py-1">
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-subtle to-line text-xs font-semibold text-ink-muted"
          aria-hidden
        >
          {initials || '?'}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">{name}</p>
          {meta.user?.email && <p className="truncate text-xs text-ink-faint">{meta.user.email}</p>}
        </div>
        <ThemeToggle />
        {signOut && (
          <Menu>
            <MenuTrigger asChild>
              <IconButton label="Account" tooltip={false}>
                <MoreHorizontal />
              </IconButton>
            </MenuTrigger>
            <MenuContent align="end" side="top">
              {meta.user && (
                <MenuLabel>
                  {ROLE_LABEL[meta.user.role]}
                  {meta.user.can_download || meta.user.is_admin ? '' : ' · view-only documents'}
                </MenuLabel>
              )}
              {mode === 'local' && (
                <MenuItem onSelect={() => setChangingPassword(true)}>
                  <KeyRound /> Change password
                </MenuItem>
              )}
              {meta.user && <MenuSeparator />}
              <MenuItem onSelect={signOut}>
                <LogOut /> Sign out
              </MenuItem>
            </MenuContent>
          </Menu>
        )}
      </div>
      {mode === 'local' && (
        <Modal open={changingPassword} onOpenChange={setChangingPassword} title="Change password">
          <PasswordForm onDone={() => setChangingPassword(false)} />
        </Modal>
      )}
    </div>
  )
}
